"use client";

import { ArrowRightIcon, XIcon } from "lucide-react";
import {
  CSSProperties,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { logEvent } from "@/service/logs/log";
import { ResourceContext } from "@/contexts/resource-context";
import { PKWPOZ_CONFERENCE_SLUG } from "./home-pkwpoz-banner-constants";

const REGISTER_URL = `https://remedium.md/konferencje/${PKWPOZ_CONFERENCE_SLUG}`;

const HOLD_MS = 4500;
const ASSETS = "/home/konfa-poz";

const SLIDES = [
  {
    title:
      "RSV u&nbsp;niemowląt: uodpornienie bierne i&nbsp;szczepienia ciężarnych",
    speakers: [{ src: `${ASSETS}/avatar-kuchar.png`, position: "50% 20%" }],
    names: "prof. dr hab. n. med. Ernest Kuchar",
  },
  {
    title: "Szybkie decyzje przy grypie, krztuścu, ospie i&nbsp;odrze",
    speakers: [
      { src: `${ASSETS}/avatar-kulus.png`, position: "22% 25%" },
      { src: `${ASSETS}/avatar-paciepnik.png`, position: "50% 15%" },
    ],
    names: "prof. dr hab. n. med. Marek Kulus, lek. Iwona Paciepnik",
  },
  {
    title: "Antybiotyk w&nbsp;POZ: kiedy naprawdę ratuje, a&nbsp;kiedy nie?",
    speakers: [{ src: `${ASSETS}/avatar-wanke.png`, position: "50% 12%" }],
    names: "dr n. med. Monika Wanke-Rytt",
  },
];

const virusStyle = (dur: string, delay?: string): CSSProperties =>
  ({
    "--konfa-dur": dur,
    ...(delay ? { "--konfa-delay": delay } : {}),
  }) as CSSProperties;

export const HomePkwpozBanner = ({
  closeBanner,
}: {
  closeBanner?: () => void;
}) => {
  const { logView } = useContext(ResourceContext);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (paused) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const id = setInterval(
      () => setActive((a) => (a + 1) % SLIDES.length),
      HOLD_MS,
    );
    return () => clearInterval(id);
  }, [paused, active]);

  const handleCtaClick = useCallback(() => {
    logEvent("home_banner_button_click", logView, {
      banner_type: "pkwpoz-konferencja",
    });
  }, [logView]);

  if (!open) return null;

  return (
    <div
      className="relative mt-10 mb-10 flex flex-col gap-3 overflow-hidden rounded-xl px-5 py-6 lg:block lg:h-[200px] lg:gap-0 lg:rounded-3xl lg:p-0"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      {/* Tło + delikatna rozjaśniająca warstwa */}
      <img
        src={`${ASSETS}/bg.png`}
        alt=""
        aria-hidden
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-[#f5f4ef]/20" />

      {/* Klik w cały baner linkuje do rejestracji */}
      <a
        href={REGISTER_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleCtaClick}
        aria-label="Zapisz się na konferencję"
        className="absolute inset-0 z-[15]"
      />

      {/* Floatujące wirusy — dekoracja (mobile + desktop, inne tory) */}
      <img
        src={`${ASSETS}/virus-teal.png`}
        alt=""
        aria-hidden
        className="konfa-float-a pointer-events-none absolute top-[-24px] right-[-24px] w-[130px] select-none lg:top-[6%] lg:right-auto lg:left-[42%] lg:w-[150px]"
        style={virusStyle("6.5s")}
      />
      <img
        src={`${ASSETS}/virus-red.png`}
        alt=""
        aria-hidden
        className="konfa-float-b pointer-events-none absolute top-[42%] right-[-12px] w-[88px] select-none lg:top-[44%] lg:right-auto lg:left-[63%] lg:w-[120px]"
        style={virusStyle("5.2s", "0.6s")}
      />
      <img
        src={`${ASSETS}/virus-blue.png`}
        alt=""
        aria-hidden
        className="konfa-float-a pointer-events-none absolute top-[36%] left-[-28px] w-[120px] select-none lg:top-[58%] lg:left-[54%] lg:w-[96px]"
        style={virusStyle("7.8s", "1.1s")}
      />

      {/* Zamknij */}
      <Button
        variant="ghost"
        size="iconSm"
        aria-label="Zamknij baner"
        onClick={() => {
          setOpen(false);
          closeBanner?.();
        }}
        className="absolute top-2 right-2 z-20 text-[#002033]/60 hover:text-[#002033]"
      >
        <XIcon size={18} />
      </Button>

      {/* Logo serii — mobile na górze, desktop absolutnie w prawym górnym rogu */}
      <div className="relative z-10 flex items-center gap-2 lg:absolute lg:top-7 lg:right-8 lg:w-[220px]">
        <img
          src={`${ASSETS}/logo-mark.svg`}
          alt=""
          aria-hidden
          className="h-[26px] w-auto lg:h-[30px]"
        />
        <div className="leading-[16px] text-black lg:leading-[18px]">
          <p className="text-[13px] lg:text-[15px]">Konferencja z serii</p>
          <p className="text-[13px] font-bold lg:text-[15px]">
            Pierwsze kroki w POZ
          </p>
        </div>
      </div>

      {/* Treść — tagline stały, tytuł + prelegenci przenikają się */}
      <div className="relative z-10 flex flex-col gap-3 lg:h-full lg:max-w-[640px] lg:justify-center lg:pl-10">
        <p className="mt-2 text-[12px] font-medium tracking-[-0.14px] text-[#1b5e81] uppercase lg:text-[14px]">
          <span>bezpłatna konferencja</span>
          <span className="hidden lg:inline">&nbsp;&middot;&nbsp;</span>
          <br className="lg:hidden" />
          <span>3 września od 12:00</span>
        </p>

        <div className="relative h-[168px] lg:h-[150px]">
          {SLIDES.map((slide, i) => (
            <div
              key={slide.names}
              aria-hidden={i !== active}
              className={cn(
                "absolute inset-0 flex flex-col justify-center gap-3 transition-opacity duration-[450ms] ease-in-out motion-reduce:transition-none",
                i === active ? "opacity-100" : "pointer-events-none opacity-0",
              )}
            >
              <h3 className="text-[24px] leading-[28px] font-medium tracking-[-0.96px] text-[#002033] lg:text-[32px] lg:leading-[39px] lg:tracking-[-1.28px]">
                <span
                  className="bg-[#E3FFAE] box-decoration-clone px-1.5"
                  dangerouslySetInnerHTML={{ __html: slide.title }}
                />
              </h3>

              <div className="flex items-center gap-2">
                <div className="flex items-center">
                  {slide.speakers.map((speaker, si) => (
                    <img
                      key={speaker.src}
                      src={speaker.src}
                      alt=""
                      aria-hidden
                      className={cn(
                        "size-8 shrink-0 rounded-full border-[1.6px] border-white bg-white object-cover",
                        si > 0 && "-ml-3",
                      )}
                      style={{ objectPosition: speaker.position }}
                    />
                  ))}
                </div>
                <p className="text-[13px] font-medium tracking-[-0.28px] text-[#1b5e81] lg:text-[14px]">
                  {slide.names}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA — mobile pełna szerokość w kolumnie, desktop absolutnie po prawej */}
      <Button
        asChild
        variant="blue"
        className="relative z-20 h-10 w-full rounded-full px-4 text-sm font-semibold uppercase lg:absolute lg:top-1/2 lg:right-8 lg:w-[220px] lg:-translate-y-1/2"
      >
        <a
          href={REGISTER_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleCtaClick}
        >
          Zapisz się!
          <ArrowRightIcon size={16} />
        </a>
      </Button>

      {/* Kropki / pigułka aktywnego slajdu */}
      <div className="relative z-20 flex justify-center gap-1.5 lg:absolute lg:right-8 lg:bottom-4 lg:left-auto lg:translate-x-0">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Pokaż slajd ${i + 1}`}
            aria-current={i === active}
            onClick={() => setActive(i)}
            className={cn(
              "h-1.5 cursor-pointer rounded-full transition-all duration-300",
              i === active ? "w-[30px] bg-[#3223f4]" : "w-1.5 bg-[#002033]/25",
            )}
          />
        ))}
      </div>
    </div>
  );
};
